import { AppDateTime } from '@macs-frontend/core/utils/datetime';
import { AppTime } from '@macs-frontend/core/utils/time';
import { UUID } from '@macs-frontend/core/utils/uuid';
import {
  ActionTeamRole,
  BudgetItem,
  BudgetItemTypes,
  ServiceTypes,
} from '@macs-frontend/web-macs/shared';
import { ActionTeamType } from '@macs-gql';
import { DataSources } from '../../company-management/action-team/enums/data-sources.enum';
import { AbstractLocation } from './abstract-location';
import { MdAction, MdBaseAction } from './action';
import { BaseActionPlanningDateAction } from './action-planning';
import { BaseEntity } from './base-entity.model';
import { BookingPerson } from './booking-person';
import { Channel } from './channel.model';
import { BaseExternalUser } from './external-user';
import { BaseProject, Project } from './project';
import { BaseRatesheet, Ratesheet } from './ratesheet.model';
import { TeamCostRole } from '@macs-frontend/features/company-management/team-cost/team-cost.types';

export interface DictionaryService {
  billablePrice: number;
  description: string;
  uuid: UUID;
  name: string;
  unitPrice: number;
  qty?: number;
}

export interface BaseATService {
  uuid: UUID;
  name: string;
  description: string;
  qty: number;
  unitPrice: number;
  billablePrice: number;
  billablePriceMargin: number;
  amount: number;
  type: ServiceTypes;
}

export interface ATService extends BaseATService {
  dictionaryService?: DictionaryService;
}

export interface DictionaryTransport {
  code: string;
  uuid: UUID;
  name: string;
  variableCost?: number;
}

export interface DictionaryMaterial {
  uuid: UUID;
  name: string;
  unitPrice: number;
  billablePrice: number;
  type: string;
}

export interface SampleUnit {
  uuid: UUID;
  name: string;
  inTray: number;
  inLayer: number;
  inPalet: number;
  isActive: boolean;
}

export interface BaseMaterialGroupItem {
  uuid: UUID;
  name: string;
  qty: number;
  estimated: number;
  transported: number;
  inTray?: number;
  inLayer?: number;
  inPalet?: number;
  inChecklist: boolean;
  iceBoxes?: number;
  iceCubes?: number;
  layersOnPallet?: number;
}

export interface BaseProjectMaterialGroupItem {
  uuid: UUID;
  name: string;
  qtyRequired: number;
  qtyLimit: number;
  totalQtyLimit?: number;
  reserveUnit?: number;
  reservePercent?: number;
  qtyTotal?: number;
  totalEstimated?: number;
  totalTransported?: number;
  unitPrice: number;
  billablePrice: number;
  retourPrice: number;
  totalBillablePrice: number;
  totalUnitPrice: number;
  createdAt?: AppDateTime;
  isHiddenForBudgetOverview?: boolean;
}

export interface BaseProjectMaterialGroup {
  uuid: UUID;
  type: MaterialGroupTypes;
  name: string;
}

export interface BaseATTransport {
  uuid: UUID;
  distance: number;
  name: string;
  distanceUnitPrice: number;
  distanceBillablePrice: number;
  totalUnitPrice: number;
  totalBillablePrice: number;
  totalDistanceUnitPrice: number;
  totalDistanceBillablePrice: number;
  costUnitPrice: number;
  costBillablePrice: number;
  totalCostUnitPrice: number;
  totalCostBillablePrice: number;
  handlingUnitPrice: number;
  handlingBillablePrice: number;
  code: string;
  qty: number;
  licensePlate?: string;
  isParentTransport: boolean;
}

export interface ATTransport extends BaseATTransport {
  ratesheet?: BaseRatesheet;
  dictionaryTransport?: DictionaryTransport;
  driver?: BaseExternalUser;
}

export interface BaseActionTeam extends BaseEntity {
  name: string;
  description: string;
  logo: string;
  normalHours: AppTime;
  hasCustomActions?: boolean;
  breakHours: AppTime;
  substractBreakHoursForHR: boolean;
  substractBreakHoursForClient: boolean;
  contactCount: number;
  compositionReservePercentage: number;
  extraCost?: number;
  billablePrice: number;
  margin: number;
  unitPrice: number;
  ccBillable?: number;
  ccMargin?: number;
  ccReal?: number;
  targetContacts?: number;
  totalContactsVal?: number;
  totalContactsPercent?: number;
  budgetVal?: number;
  budgetPercent?: number;
  actions?: number;
  totalCost?: number;
  totalActions?: number;
  realActionsQty?: number;
  realTeamCost?: number;
  isCustom?: boolean;
  customNumber?: number;
  compositionRolesTotalBillablePrice?: number;
  compositionRolesTotalUnitPrice?: number;
  servicesTotalBillablePrice?: number;
  servicesTotalUnitPrice?: number;
  transportsTotalBillablePrice?: number;
  transportsTotalUnitPrice?: number;
  startDate?: AppDateTime;
  endDate?: AppDateTime;
  startDateUTC?: AppDateTime;
  endDateUTC?: AppDateTime;
  viaCompanyStartDate?: AppDateTime;
  viaCompanyEndDate?: AppDateTime;
  viaCompanyStartDateUTC?: AppDateTime;
  viaCompanyEndDateUTC?: AppDateTime;
  directToEventStartDate?: AppDateTime;
  directToEventEndDate?: AppDateTime;
  directToEventStartDateUTC?: AppDateTime;
  directToEventEndDateUTC?: AppDateTime;
  type: ActionTeamType;
}

export interface ScheduleAssignmentTag {
  uuid: UUID;
  name: string;
  code: string | null;
  color: string;
}

export interface BaseScheduleAssignment {
  uuid: UUID;
  name: string;
  startTime: number;
  endTime: number;
  duration: number;
  type: ScheduleAssignmentTypes;
  color: string;
  startDay: number;
  endDay: number;
  startLocationsAsString: string;
  tag?: ScheduleAssignmentTag | null;
}

export interface BaseScheduleStartLocation {
  uuid: UUID;
  name: string;
  address: string;
  isDefault: boolean;
}

export interface ScheduleAssignment extends BaseScheduleAssignment {
  scheduleStartLocations: BaseScheduleStartLocation[];
}

export interface ScheduleStartLocation extends BaseScheduleStartLocation {
  scheduleAssignments?: ScheduleAssignment[];
}

export enum ScheduleAssignmentTypes {
  PreAction = 'PRE_ACTION',
  OnEvent = 'ON_EVENT',
  AfterAction = 'AFTER_ACTION',
}

export interface BaseCompositionRole {
  uuid: UUID;
  costPrice: number;
  sellPrice: number;
  defaultCostPricePerHour: number;
  defaultSellPricePerHour: number;
  costPricePerHour: number;
  sellPricePerHour: number;
  overtimeCostPricePerHour: number;
  overtimeSellPricePerHour: number;
  transportCostPricePerDistanceUnit: number;
  transportSellPricePerDistanceUnit: number;
  transportDistance: number;
  quantity: number;
  fullness: number;
  pendingBookingPersonCount?: number;
  bookedBookingPersonCount?: number;
  amount: number;
  roleName: string;
  normalRatePercentage: number;
  overtimeRatePercentage: number;
  holidayHourPricePercentage: number;
  holidayHourPriceUnitPrice: number;
  holidayHourPriceBillablePrice: number;
  holidayOvertimePricePercentage: number;
  holidayOvertimePriceUnitPrice: number;
  holidayOvertimePriceBillablePrice: number;
  ownCarViaUnitPrice: number;
  ownCarViaBillablePrice: number;
  ownCarDirectUnitPrice: number;
  ownCarDirectBillablePrice: number;
  dataSource?: DataSources;
}

export interface CompositionRoleActionTeamAction extends MdBaseAction {
  actionPlanningDate: BaseActionPlanningDateAction;
  actionLocation?: AbstractLocation;
}

export interface CompositionRoleActionTeam
  extends BaseActionTeam,
    Pick<ActionTeam, 'scheduleStartLocations'> {
  action?: CompositionRoleActionTeamAction;
}

export interface CompositionRole extends BaseCompositionRole {
  bookedBookingPersons?: BookingPerson[];
  pendingBookingPersons?: BookingPerson[];
  bookingPersons?: BookingPerson[];
  actionTeam?: CompositionRoleActionTeam;
  actionTeamRole?: ActionTeamRole;
  teamCostRole?: TeamCostRole;
  ratesheet: Pick<BaseRatesheet, 'uuid' | 'name'>;
  scheduleStartLocation: null | ScheduleStartLocation;
}

export interface MaterialGroupItem extends BaseMaterialGroupItem {
  dictionaryMaterial?: DictionaryMaterial;
  dictionarySampleUnit?: SampleUnit;
  projectMaterialGroupItem?: Omit<ProjectMaterialGroupItem, 'materialGroupItems'>;
  projectMaterialGroup?: BaseProjectMaterialGroup;
  actionTeam?: BaseActionTeam;
  // Fields surfaced by `_shape=@action-materials` on /projects/action_teams/{uuid}.
  pictureUrl?: string | null;
  clientRemarks?: string | null;
  // ST044: the material's one-time "material list remark". Carried on the rows
  // the action detail page builds from materials-ms.
  listRemarks?: string | null;
  returnDate?: string | null; // ISO date string
}

export interface ProjectMaterialGroupItem extends BaseProjectMaterialGroupItem {
  budgetItem?: BudgetItem<BudgetItemTypes.MATERIAL_COST>;
  projectMaterialGroup: BaseProjectMaterialGroup;
  dictionaryMaterial?: DictionaryMaterial;
  dictionarySampleUnit?: SampleUnit;
  materialGroupItems?: MaterialGroupItem[];
}

export interface ProjectMaterialGroup extends BaseProjectMaterialGroup {
  projectMaterialGroupItems: ProjectMaterialGroupItem[];
}

export interface ActionTeamProject
  extends BaseProject,
    Pick<
      Project,
      'projectMaterialGroups' | 'projectGroup' | 'briefing' | 'client' | 'company'
    > {}

export interface BaseMaterialGroup {
  uuid: UUID;
  name: string;
  type: MaterialGroupTypes;
}

export interface MaterialGroup extends BaseMaterialGroup {
  materialGroupItems: MaterialGroupItem[];
}

export interface ActionTeam extends BaseActionTeam {
  action?: MdAction;
  channel: Channel;
  materialGroups: MaterialGroup[];
  materialGroupItems: MaterialGroupItem[];
  services: ATService[];
  transports: ATTransport[];
  project: ActionTeamProject;
  scheduleStartLocations: ScheduleStartLocation[];
  scheduleAssignments: ScheduleAssignment[];
  compositionRoles: CompositionRole[];
  parent?: BaseActionTeam;
  ratesheet?: Ratesheet;
  transportRatesheet?: Ratesheet;
  compositionRoleRatesheet?: Ratesheet;
  warehouseRatesheet?: Ratesheet;
  serviceRatesheet?: Ratesheet;
  budgetItem?: BudgetItem<BudgetItemTypes.TEAM_COST>;
  feesBudgetItem?: BudgetItem<BudgetItemTypes.FEES>;
}

export enum SaveActionTeamKind {
  One,
  ExceptCustom,
  All,
}

export enum ActionTeamTabs {
  GeneralInfo = 'general',
  TeamSchedule = 'schedule',
  TeamSetup = 'setup',
  Materials = 'materials',
  Services = 'services',
  Warehouse = 'warehouse',
  Transport = 'transport',
  Overview = 'overview',
}

export enum CalcRoleByTypes {
  HourBillablePrice = 'HourBillablePrice',
  DistanceBillablePrice = 'DistanceBillablePrice',
}

export enum MaterialGroupTypes {
  Disposable = 'DISPOSABLE',
  Recurring = 'RECURRING',
  SampleUnit = 'SAMPLE',
  Clothes = 'CLOTHES',
}

export enum MaterialsSaveStatuses {
  NotSaved = 1,
  Saving,
  Saved,
}
